/**
 * Ryan O'Dowd
 * 2022-08-05
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useEffect,
  useState,
} from 'react';
import {
  fetchFriends,
  getRequestHeaders,
  setLastViewedFollowersTimestamp,
} from '@utils/store/actions';
import Button from '@components/common/Button';
import ButtonGroup from '@components/common/ButtonGroup';
import EmptyScreen from '@components/common/EmptyScreen';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import Header from '@components/Header';
import OfflineBanner from '@components/common/OfflineBanner';
import ScreenWrapper from '@containers/ScreenWrapper';
import Searchbar from '@components/common/Searchbar';
import SectionList from '@components/common/SectionList';
import User from './User';
import {
  View,
} from 'react-native';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import {
  useNetInfo,
} from '@react-native-community/netinfo';
import utilities from '@utils/utilities';

const Friends = () => {
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const locationState = hooks.useLocationState();
  const styles = hooks.useStyles(getStyles);
  const netInfo = useNetInfo();

  hooks.useBackButton(() => {
    return false;
  });

  const [_searchOpen, setSearchOpen] = useState(locationState.openTab === 'find');
  const [_searchText, setSearchText] = useState('');

  const following = hooks.useTypedSelector((state) => state.usersFollowing);
  const followingIds = following.map((u) => u.id);
  const followers = hooks.useTypedSelector((state) => state.userFollowers);

  following.sort((a, b) => new Date(b.last_active).getTime() - new Date(a.last_active).getTime());
  followers.sort((a, b) => new Date(b.last_active).getTime() - new Date(a.last_active).getTime()); // @TODO: followers.sort((a, b) => by reverse request date?):

  const [_visibleList, setVisibleList] = useState(locationState.openTab ?? 'following');
  const [_searchResults, setSearchResults] = useState([]);
  // @TODO: const [_recommendedFriends, setRecommendedFriends] = useState([]);
  const [_isFetchingResults, setIsFetchingResults] = useState(false);

  useEffect(() => {
    dispatch(fetchFriends());
    // @TODO: dispatch(fetchRecommendedFriends());
  }, [dispatch]);

  const _getSearchResults = async (queryString) => {
    const response = await fetch(`${globals.apiUrl}/users/find?q=${encodeURIComponent(queryString)}`, {
      headers: await getRequestHeaders(),
    });
    const {users} = await response.json();
    setSearchResults(users);
    setIsFetchingResults(false);
  };

  /* @TODO:
  const _getRecommendedFriends = async () => {
    const response = await fetch(`${globals.apiUrl}/users/recommendations`, {
      headers: await getRequestHeaders(),
    });
    const {users} = await response.json();
    setSearchResults(users);
    setIsFetchingResults(false);
  };
  */

  useEffect(() => {
    if (_visibleList !== 'find' || !_searchText || !netInfo.isInternetReachable) { // @TODO: require at least 3 characters to be typed as well (and update empty-screen messages accordingly)
      setIsFetchingResults(false);
      setSearchResults([]);
      return;
    }

    setIsFetchingResults(true);

    const networkDelay = 300;
    const networkCallTimer = setTimeout(() => {
      _getSearchResults(_searchText);
    }, networkDelay);

    return () => {
      clearTimeout(networkCallTimer);
    };
  }, [_visibleList, _searchText, netInfo.isInternetReachable]);

  /* @TODO:
  useEffect(() => {
    _getRecommendedFriends();
  }, [following.length, followers.length]); // @TODO: could maybe be more judicious about how often this is called
  */

  const searchItem = (
    <Header.Icon
      icon='magnify'
      onPress={() => {
        const newSearchOpenState = !_searchOpen;
        if (!newSearchOpenState) {
          setSearchText('');
        }
        setSearchOpen(newSearchOpenState);
      }}
    />
  );

  let sectionListData = [];

  const _filterList = (list) => {
    return list.filter((u) => {
      if (!_searchText || _visibleList === 'find') {
        return true;
      }

      const searchTerms = _searchText.toLocaleLowerCase().split(' ');
      return searchTerms.every((searchTerm) => {
        if (utilities.normalizeApostrophes(u.display_name.toLocaleLowerCase()).includes(searchTerm)) {
          return true;
        }
        if (utilities.normalizeApostrophes(u.username.toLocaleLowerCase()).includes(searchTerm)) {
          return true;
        }

        return false;
      });
    });
  };

  if (_visibleList === 'following') {
    sectionListData = [{
      key: 'following',
      title: i18n('following'),
      data: _filterList(following),
    }];
  } else if (_visibleList === 'followers') {
    sectionListData = [{
      key: 'followers',
      title: i18n('followers'),
      data: _filterList(followers),
    }];
  } else {
    // find page
    sectionListData = [];
    if (_searchResults.length) {
      sectionListData.push({
        key: 'searchResults',
        title: i18n('searchResults'),
        data: _searchResults,
      });
    }
    /* @TODO:
    if (_recommendedFriends.length) {
      sectionListData.push({
        key: 'recommendedFriends',
        title: i18n('recommendations'),
        data: _recommendedFriends,
      });
    }
    */
  }

  const backIcon = <Header.BackIcon />;

  return (
    <ScreenWrapper>
      <Header
        title={i18n('friends')}
        leftItems={[backIcon]}
        rightItems={[searchItem]}// @TODO: and sort (by streak, wrinkles, peanuts, last_active...default in redux should be last_active) // @TODO: also add overflow menu for viewing all blocked users (menu only available if there are blocked users...in these cases, show user card, but it's not tappable)
      />
      {_searchOpen &&
        <Searchbar
          onChangeText={(searchText) => setSearchText(searchText)}
          value={_searchText}
          placeholder={i18n('usernameOrDisplayName')}
        />
      }
      <FullScreenScrollView>
        {_visibleList === 'find' && <OfflineBanner containerStyle={styles.offlineBanner} message={i18n('searchFunctionalityDisabled')} />}
        <View style={styles.buttonGroupWrapper}>
          <ButtonGroup
            options={[
              {
                title: i18n('following'), // @TODO: better name
                value: 'following',
              },
              {
                title: i18n('followers'), // @TODO: better name
                value: 'followers',
                // @TODO: show badge if new followers since lastViewedFollowersTimestamp
              },
              {
                title: i18n('find'), // @TODO: better name // @TODO: only an option if user already has at least 1 following
                value: 'find',
              },
            ]}
            selectedValue={_visibleList}
            onSelect={(newValue) => {
              if (newValue === 'find') {
                setSearchOpen(true);
              } else if (newValue === 'followers') {
                dispatch(setLastViewedFollowersTimestamp(new Date().getTime()));
              }
              setVisibleList(newValue);
            }}
          />
        </View>
        {sectionListData[0]?.data.length > 0
          ? (
            <SectionList// @TODO: does this need to be a sectionlist? flatlist should be sufficient
              contentContainerStyle={styles.list}
              keyboardDismissMode='on-drag'
              sections={sectionListData}
              renderItem={({item}) => {
                return (
                  // @TODO: put badge on new followers since lastViewedFollowersTimestamp
                  <User
                    {...item}
                    isFollowing={followingIds.includes(item.id)}// @TODO: not accurate...
                  />
                );
              }}
              renderSectionHeader={() => {}}
            />
          ) : (
            (!!_searchText &&
              <EmptyScreen.Wrapper avoidKeyboard={true}>
                <EmptyScreen.Text>
                  {_visibleList === 'find' && _isFetchingResults
                    ? i18n('fetchingSearchResults')
                    : i18n('noMatches', _searchText.trim())
                  }
                </EmptyScreen.Text>
                {_visibleList !== 'find' &&
                  <Button
                    mode='contained'
                    theme={styles.colors.accent}
                    onPress={() => setVisibleList('find')}
                  >
                    {i18n('searchForUser')}
                  </Button>
                }
              </EmptyScreen.Wrapper>
            )
          )
        }
        {!sectionListData[0]?.data.length && _visibleList !== 'find' && !_searchText &&
          <EmptyScreen.Wrapper avoidKeyboard={true}>
            <EmptyScreen.Text>
              {_visibleList === 'following' ? i18n('notFollowing') : i18n('noFollowers')}
            </EmptyScreen.Text>
          </EmptyScreen.Wrapper>
        }
        {!sectionListData[0]?.data.length && _visibleList === 'find' && !_searchText &&
          <EmptyScreen.Wrapper avoidKeyboard={true}>{/* @TODO: replace this emptyScreenWrapper with recommendations */}
            <EmptyScreen.Text>
              {i18n('searchForFriends')}
            </EmptyScreen.Text>
          </EmptyScreen.Wrapper>
        }
      </FullScreenScrollView>
    </ScreenWrapper>
  );
};

export default Friends;
